import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Separator } from './ui/separator';
import { 
  MapPin, 
  Phone, 
  Mail, 
  Clock, 
  Recycle,
  Award
} from 'lucide-react';
import USAFlag from './icons/USAFlag';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-black text-white">
      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* Company Info with Real Logo */}
          <div className="space-y-6">
            <div className="flex items-center justify-start">
              <img 
                src="/images/automats-logo-footer.webp" 
                alt="AutoMats Logo" 
                className="h-64 w-auto object-contain"
              />
            </div>
            <p className="text-gray-300 leading-relaxed">
              Transforming waste into value since 1985. Better for your car, better for your margins, better for the planet.
            </p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center space-x-2 text-automats-eco-green">
                <Recycle className="h-4 w-4" />
                <span>100% Recycled Materials</span>
              </div>
              <div className="flex items-center space-x-2 text-automats-eco-green">
                <Award className="h-4 w-4" />
                <span>40+ Years Experience</span>
              </div>
              <div className="flex items-center space-x-2 text-automats-eco-green">
                <USAFlag className="h-4 w-4" />
                <span>Made in USA</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/products" className="text-gray-300 hover:text-automats-red transition-colors font-body">
                  Product Explorer
                </Link>
              </li>
              <li>
                <Link to="/brand-story" className="text-gray-300 hover:text-automats-red transition-colors font-body">
                  Our Story
                </Link>
              </li>
              <li>
                <Link to="/dealer-hub" className="text-gray-300 hover:text-automats-red transition-colors font-body">
                  Resource Hub
                </Link>
              </li>
              <li>
                <Link to="/impact" className="text-gray-300 hover:text-automats-red transition-colors font-body">
                  Eco Impact Calculator
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-gray-300 hover:text-automats-red transition-colors font-body">
                  Contact Us
                </Link>
              </li>
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-6">Services</h4>
            <ul className="space-y-3">
              <li>
                <a 
                  href="mailto:sales@automats.us?subject=Custom Manufacturing Inquiry&body=Hello, I would like to learn more about custom manufacturing services."
                  className="text-gray-300 hover:text-automats-red transition-colors font-body"
                >
                  Custom Manufacturing
                </a>
              </li>
              <li>
                <a 
                  href="mailto:sales@automats.us?subject=Private Labeling Inquiry&body=Hello, I would like to learn more about private labeling options."
                  className="text-gray-300 hover:text-automats-red transition-colors font-body"
                >
                  Private Labeling
                </a>
              </li>
              <li>
                <a 
                  href="mailto:sales@automats.us?subject=Dealer Program Inquiry&body=Hello, I would like to learn more about your dealer program."
                  className="text-gray-300 hover:text-automats-red transition-colors font-body"
                >
                  Dealer Program
                </a>
              </li>
              <li>
                <a 
                  href="mailto:sales@automats.us?subject=Bulk Order Inquiry&body=Hello, I would like to place a bulk order."
                  className="text-gray-300 hover:text-automats-red transition-colors font-body"
                >
                  Bulk Ordering
                </a>
              </li>
              <li>
                <a 
                  href="mailto:sales@automats.us?subject=Technical Support Request&body=Hello, I need technical support."
                  className="text-gray-300 hover:text-automats-red transition-colors font-body"
                >
                  Technical Support
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info - CORRECT INFORMATION */}
          <div>
            <h4 className="text-lg font-heading font-semibold mb-6">Get in Touch</h4>
            <div className="space-y-4">
              <div className="flex items-start space-x-3">
                <MapPin className="h-5 w-5 text-automats-red mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-300 font-body">
                  <p>506 Martha Sue Drive</p>
                  <p>Dalton, GA 30722</p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                <Phone className="h-5 w-5 text-automats-red flex-shrink-0" />
                <a href="tel:+18005700704" className="text-sm text-gray-300 hover:text-white font-body">
                  800-570-0704
                </a>
              </div>
              <div className="flex items-center space-x-3">
                <Mail className="h-5 w-5 text-automats-red flex-shrink-0" />
                <a href="mailto:sales@automats.us" className="text-sm text-gray-300 hover:text-white font-body">
                  sales@automats.us
                </a>
              </div>
              <div className="flex items-start space-x-3">
                <Clock className="h-5 w-5 text-automats-red mt-0.5 flex-shrink-0" />
                <div className="text-sm text-gray-300 font-body">
                  <p>Mon-Fri: 8:00 AM - 5:00 PM EST</p>
                </div>
              </div>
            </div>

            <div className="mt-6">
              <a 
                href="mailto:sales@automats.us?subject=Quote Request&body=Hello, I would like to request a quote for AutoMats products."
                className="w-full bg-automats-red hover:bg-red-700 text-white px-6 py-3 rounded-lg font-body font-semibold transition-all duration-300 hover:scale-105 block text-center"
              >
                Request Quote
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <Separator className="bg-gray-700" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
          <div className="text-sm text-gray-400 font-body">
            © {currentYear} AutoMats. All rights reserved. | Proudly Made in USA since 1985
          </div>
          <div className="flex space-x-6 text-sm font-body">
            <a 
              href="mailto:sales@automats.us?subject=Privacy Policy Inquiry&body=Hello, I have questions about your privacy policy."
              className="text-gray-400 hover:text-white transition-colors"
            >
              Privacy Policy
            </a>
            <a 
              href="mailto:sales@automats.us?subject=Terms of Service Inquiry&body=Hello, I have questions about your terms of service."
              className="text-gray-400 hover:text-white transition-colors"
            >
              Terms of Service
            </a>
            <Link 
              to="/impact"
              className="text-gray-400 hover:text-white transition-colors"
            >
              Sustainability Impact
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;